import React, {useEffect, useMemo, useState} from "react";

import {Button, Col, Row, Spin} from "antd";
import {ArrowLeftOutlined, SyncOutlined} from '@ant-design/icons';
import {DollarIcon, ItemsIcon} from "@components/icons/Icons";
import {Contain} from "@components/layout";
import useSearchView from "@hooks/useSearchView";
import {formatDateTiny, formatGrade, formatMoney, formatNumber} from "@lib/format";
import {useNavigate} from "react-router-dom";
import {useGetUserSeriesQuery, useRefreshUserSeriesFmvMutation} from "@mwServices/Series";
import MyCollectionIssues from "@mwPages/my-collection/issues/MyCollectionIssues";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {FmvPrice} from "@components/fmv-price";
import {showResponse} from "@services/Api";
import moment from "moment";
import {scrollToTop} from "@lib/utils";
import {SCREEN, useWindowSize} from "@components/window-size-provider";
import {BoxesAddDrawer} from "@components/boxes-add-drawer";
import {isEqual} from "underscore";

const MyCollectionSeriesIssues = () => {
    const windowDimensions = useWindowSize();
    const { view } = useSearchView();
    const navigate = useNavigate();
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [postRefreshFmv, refreshFmvResponse] = useRefreshUserSeriesFmvMutation();
    const [addBox, setAddBox] = useState<{show: boolean, issue_ids: number[], series_ids: number[]}>({show: false, issue_ids: [], series_ids: []});

    useEffect(() => {
        showResponse(refreshFmvResponse, 'Series fmv refreshed.', 'Sorry, there was a problem refreshing the series FMV.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshFmvResponse]);

    const subscriptionType = useSubscriptionType();
    const showTotalFmv = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);
    const showStats = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);

    const seriesQuery = useGetUserSeriesQuery({filters: {series: view ? [Number(view)] : undefined, include_most_valuable: true}});

    const [series, setSeries] = useState<any>(null);

    useEffect(() => {
        if(seriesQuery.data && seriesQuery.data.series){
            if(seriesQuery.data.series.length > 0) {
                const newSeries = seriesQuery.data.series[0];
                if(!isEqual(newSeries, series)){
                    console.log('Series reloaded.');
                    setSeries(newSeries);
                }
            }else{
                navigate('my-collection/?view=series');
            }
        }
    }, [seriesQuery]);

    const iconStyle = useMemo(() => windowDimensions.up(SCREEN.MD) ? {color: '#2f404a', width: 50, height: 50, marginRight: 10} : {color: '#2f404a', width: 30, height: 30, marginRight: 0, padding: 5} , [windowDimensions])
    const iconClass = useMemo(() => windowDimensions.up(SCREEN.MD) ? "whiteCircleIconSmall" : "whiteCircleIconTiny", [windowDimensions]);

    const showMostValuable = useMemo(() => windowDimensions.up(SCREEN.MD), [windowDimensions]);
    const fmvUpdated = useMemo(() => series && series.fmv_updated ? moment(series.fmv_updated).toDate() : null, [series]);

    useEffect(() => scrollToTop(), [series]);

    return (
        <div className={"myCollectionIssues "+(windowDimensions.up(SCREEN.MD) ? 'large-screen' : '')} >
            <Spin spinning={seriesQuery.isLoading }>
                {series ? (<>
                    <div
                        className="collection-issues-header"
                        style={{
                            background: `linear-gradient( to top right, rgba(14, 32, 45, 1), rgba(14, 32, 45, 0.5) ),url(${series && series.full_image}) 0 25% no-repeat`,
                            backgroundSize: "cover"
                        }}
                    >
                        <Row className="height-0">
                            <Col span={12} className="top-col-1">
                                <div className="col-1-inner">
                                    <Button type="text" size={windowDimensions.up(SCREEN.MD) ? "large" : 'middle'} className="text-white" onClick={() => navigate('my-collection/?view=series')}>
                                        <ArrowLeftOutlined className={windowDimensions.up(SCREEN.MD) ? "font-20" : 'font-12'} /> Back
                                    </Button>
                                </div>
                            </Col>
                            <Col span={12} className="top-col-2">
                                <div className="corner-triangle col-2-inner">
                                    <ItemsIcon
                                        className="whiteCircleIconSmall"
                                        style={ windowDimensions.up(SCREEN.MD) ?
                                            {...iconStyle, border: 'solid 1px #2f404a', marginLeft: "78px", marginTop: "20px"} :
                                            {...iconStyle, border: 'solid 1px #2f404a', marginLeft: "35px", marginTop: "5px"}
                                        }
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <div className="main-row">
                                <div className="main-row-inner">
                                    <span className="heading-text">{series && series.series_title}</span><br/>
                                    <span className="heading-text">{series && (series.publisher + ' | ' + series.year_began + (series.year_ended && series.year_ended !== series.year_began ? ' - '  + series.year_ended : ''))}</span><br/>
                                </div>

                            </div>
                        </Row>

                        <Row className="bottom-row">
                            <Col span={24}>
                                <div className="inner-row">
                                    <div className="justify-spacer" />
                                    <div
                                        style={{maxWidth: showMostValuable ? '32%' : '49%'}}
                                        className={"value-cell " + (series.fmv_can_update ? 'cursor-pointer' : '')}
                                        onClick={(showTotalFmv && series.fmv_can_update) ? async () => {
                                            await postRefreshFmv({series_id: series.series_id})
                                        } : undefined}
                                    >
                                        <DollarIcon style={iconStyle} className={iconClass} />
                                        <div className={"cell-text mar-left-5 " + (showTotalFmv && series.fmv_can_update && fmvUpdated ? 'mar-top-15 ' : '')}>
                                            Total FMV:&nbsp;

                                            {showTotalFmv ? (
                                                <Spin spinning={refreshFmvResponse.isLoading}>
                                                    <Row className="flex-align-center">
                                                        <FmvPrice
                                                            value={series.fmv}
                                                            stale={series.fmv_is_stale}
                                                            staleInfo="FMV data is stale, please update."
                                                        />
                                                        {series.fmv_can_update && (<SyncOutlined className={'fmv-update can-update-white'} />)}
                                                    </Row>
                                                    {(series.fmv_can_update && fmvUpdated) && (
                                                        <Row className="flex-align-center">
                                                            <span className='font-10 text-grey'>Updated: {formatDateTiny(fmvUpdated)}</span>
                                                        </Row>
                                                    )}
                                                </Spin>
                                            ) : (
                                                <a className="upgrade-link" href="/plans/unlimited/">Upgrade</a>
                                            )}
                                        </div>
                                    </div>
                                    <div className="vertical-line" />
                                    <div
                                        className="value-cell"
                                        style={{maxWidth: showMostValuable ? '32%' : '49%'}}
                                    >
                                        <ItemsIcon style={iconStyle} className={iconClass} />
                                        <div className="cell-text mar-left-5">
                                             Issue Count:&nbsp;
                                             <b>{series && formatNumber(series.overview.issues_by_number)} of {series && formatNumber(series.issue_count)}</b>
                                        </div>
                                    </div>
                                    {showMostValuable && (
                                        <>
                                            <div className="vertical-line" />
                                            <div
                                                style={{maxWidth: showMostValuable ? '32%' : '49%'}}
                                                className="value-cell"
                                            >
                                                <div className="flex-row most-valuable" >
                                                    <div  className="most-valuable-title">Most<br/>Valuable</div>
                                                    <div className="most-valuable-vertical-line" />
                                                    <div className={"most-valuable-data mar-left-5 " + (showTotalFmv ? '' : 'pad-top-10 ')} >
                                                        {showStats ? (
                                                            <>
                                                                {series && series.most_valuable && (
                                                                    <>
                                                                        <div className="most-valuable-text mar-bot-5">
                                                                            {series.most_valuable.comic_title}
                                                                        </div>
                                                                        <div className="most-valuable-text">
                                                                            {series.most_valuable.graded_by}&nbsp;
                                                                            {formatGrade(series.most_valuable.grade)}
                                                                            &nbsp;|&nbsp;
                                                                            {formatMoney(series.most_valuable.estval)}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <a className="upgrade-link" href="/plans/unlimited/">Click to Upgrade</a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <div className="justify-spacer" />
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <Contain className="pad-bot-20">
                        <MyCollectionIssues
                            showSeries={series}
                            showSearchIssue={true}
                            search={debouncedSearch}
                            setSearch={(newSearch) => {
                                setDebouncedSearch(newSearch);
                            }}
                            searchPlaceholder={"Issue #"}
                            setAddBox={setAddBox}
                         />
                    </Contain>
                </>) : null }
            </Spin>

            <BoxesAddDrawer visible={addBox.show} issue_ids={addBox.issue_ids} series_ids={addBox.series_ids} onClose={() => {setAddBox({show: false, issue_ids: [], series_ids: []})}} />
        </div>
    );
};

export default MyCollectionSeriesIssues;